import { createContext, Dispatch, SetStateAction } from 'react'

import amyCharacterSrc from '~/assets/images/characters/amy.webp'
import ashaCharacterSrc from '~/assets/images/characters/asha.webp'
import claraCharacterSrc from '~/assets/images/characters/clara.webp'
import danCharacterSrc from '~/assets/images/characters/dan.webp'
import jaxCharacterSrc from '~/assets/images/characters/jax.webp'
import louisCharacterSrc from '~/assets/images/characters/louis.webp'
import myraCharacterSrc from '~/assets/images/characters/myra.webp'
import oliviaCharacterSrc from '~/assets/images/characters/olivia.webp'
import richardCharacterSrc from '~/assets/images/characters/richard.webp'
import {
  EAgentLanguage,
  EChatbotTheme,
  ELoopingAudio,
  EPreConnectMessageSound,
  EPreviewTab,
  NOTIFICATION_TYPE_NONE,
  TNotificationsEmailInputContext,
} from '~/features/settings/models'
import { ENotificationType } from '~/models/features'
import rem from '~/utils/ui'

export const CALENDLY_REGEX =
  /(?:https?:\/\/)?(?:www\.)?calendly\.com\/([a-zA-Z0-9_-]+)(?:\/[\w-]+)?/
export const CALENDLY_LINK = 'https://calendly.com/'
export const DEFAULT_SERVICE_NAME = 'DEFAULT'
export const WEBSITE_ANALYSIS_TIMEOUT = 20000
export const ANALYSIS_COMPLETE_TIMEOUT = 3
export const DEFAULT_TEMPERATURE = 0.5
export const CHANGE_WEBSITE_MESSAGE =
  "Changing website will overwrite your assistant's settings, potentially erasing customizations you have made previously. Are you sure you want to continue?"
export const DELETE_WEBSITE_MESSAGE =
  'Are you sure you want to delete? Your assistant will no longer be trained on your website if deleted.'
export const CHANGE_SERVICE_MESSAGE =
  "We can update your questions and facts based on this new service. Do you want us to change those, overwriting any customizations you've added?"
export const DEFAULT_PHONE_PATIENCE = 100
export const MAX_PHONE_GREETING_LENGTH = 250
export const MIN_PHONE_GREETING_LENGTH = 30
export const MIN_PRE_CONNECT_MESSAGE_LENGTH = 10
export const MAX_PRE_CONNECT_MESSAGE_LENGTH = 160
export const MAX_PHONE_PATIENCE = 920
export const DEFAULT_TTS_SPEED = 1
export const MIN_TTS_SPEED = 0.7
export const MAX_TTS_SPEED = 1.2
export const MAX_OUTAGE_RESPONSE_LENGTH = 400
export const MAX_RULES_LENGTH = 3000
export const MAX_CTA_TEXT_LENGTH = 300
export const MAX_FOLLOW_UP_TEXT_LENGTH = 190
export const MAX_FOLLOW_UP_TEXT_TEMPLATE_LENGTH = 500
export const DEFAULT_POPUP_ICON_BACKGROUND_COLOR = '#a8e6e4'
export const ChatPreviewTabContext = createContext<{
  tab: EPreviewTab
  setTab: Dispatch<SetStateAction<EPreviewTab>>
} | null>(null)
export const FollowUpTextSettingsContext = createContext<{
  showFollowUpTemplate: boolean
  setShowFollowUpTemplate: Dispatch<SetStateAction<boolean>>
} | null>(null)
export const NotificationsEmailInputContext =
  createContext<TNotificationsEmailInputContext | null>(null)
export const CHAT_SETTINGS_TAB_PREVIEW_MAP = {
  [EChatbotTheme.simple]: EPreviewTab.simple,
  [EChatbotTheme.linkView]: EPreviewTab.linkView,
}
export const DEFAULT_WAVE_ICON_STYLES = {
  padding: rem(6),
  backgroundColor: DEFAULT_POPUP_ICON_BACKGROUND_COLOR,
}
export const VOICE_OPTIONS = [
  {
    id: 'ftsyff0MFCU1XIAcYCvJ',
    src: oliviaCharacterSrc,
    name: 'Jacqueline',
    portalDescription: 'casual and clear',
    genrer: 'Female',
    language: 'English',
  },
  {
    id: 'grkOBjhN4PebqeZFaEUR',
    src: myraCharacterSrc,
    name: 'Myra',
    portalDescription: 'upbeat and lively',
    genrer: 'Female',
    language: 'English',
  },
  {
    id: 'b3WmawtkQsLsPEkjDF1n',
    src: amyCharacterSrc,
    name: 'Gina',
    portalDescription: 'calm and slower pace',
    genrer: 'Female',
    language: 'English',
  },
  {
    id: 'FISo3sWdWP0bALUdgh5x',
    src: danCharacterSrc,
    name: 'Dan',
    portalDescription: 'fast and crisp',
    genrer: 'Male',

    language: 'English',
  },
  {
    id: 'XXYRcwXlEHZ1DbdlGWh6',
    src: jaxCharacterSrc,
    name: 'Jax',
    portalDescription: 'mature and deep',
    genrer: 'Male',
    language: 'English',
  },
  {
    id: 'UgBBYS2sOqTuMpoF3BR0',
    src: richardCharacterSrc,
    name: 'Mark',
    portalDescription: 'casual and friendly',
    genrer: 'Male',
    language: 'English',
  },
  {
    id: 'UOIqAnmS11Reiei1Ytkc',
    src: claraCharacterSrc,
    name: 'Carla',
    portalDescription: 'native Spanish speaker',
    genrer: 'Female',
    language: 'Spanish',
  },
  {
    id: 'gHu9GtaHOXcSqFTK06ux',
    src: ashaCharacterSrc,
    name: 'Asha',
    portalDescription: 'native Hindi speaker',
    genrer: 'Female',
    language: 'Hindi',
  },
  {
    id: '1a3lMdKLUcfcMtvN772u',
    src: louisCharacterSrc,
    name: 'Louis',
    portalDescription: 'native French speaker',
    genrer: 'Male',
    language: 'French',
  },
]

export const LANGUAGE_OPTIONS = Object.entries(EAgentLanguage).map(
  ([label, value]) => ({ label, value }),
) as Array<{ label: string; value: string }>

export const LOOPING_AUDIO_OPTIONS = [
  { label: 'None', value: ELoopingAudio.off },
  { label: 'Office background', value: ELoopingAudio.office },
  { label: 'Call center background', value: ELoopingAudio.callCenter },
  { label: 'Restaurant background', value: ELoopingAudio.restaurant },
  { label: 'Typing', value: ELoopingAudio.typing },
] as Array<{ label: string; value: string }>

export type TPreConnectMessageSoundOption = {
  description: string
  label: string
  value: EPreConnectMessageSound
}

export const PRE_CONNECT_MESSAGE_SOUND_OPTIONS: TPreConnectMessageSoundOption[] =
  [
    {
      description: 'One short tone',
      label: 'Single beep',
      value: EPreConnectMessageSound.beep1,
    },
    {
      description: 'Two quick notes, like a doorbell',
      label: 'Two-tone chime',
      value: EPreConnectMessageSound.twoTone1,
    },
    {
      description: 'About a second of music',
      label: 'Hold music',
      value: EPreConnectMessageSound.music1,
    },
    {
      description: 'A brief pause, no sound',
      label: 'Silence',
      value: EPreConnectMessageSound.silence,
    },
  ]

export const MAX_LENGTH_NOTIFICATION_EMAIL = 2000

export const COUNTRY_OPTIONS = [
  {
    label: 'Brazil',
    value: '55',
  },
  {
    label: 'China',
    value: '86',
  },
  {
    label: 'Columbia',
    value: '57',
  },
  {
    label: 'France',
    value: '33',
  },
  {
    label: 'Germany',
    value: '49',
  },
  {
    label: 'Greece',
    value: '30',
  },
  {
    label: 'India',
    value: '91',
  },
  {
    label: 'Indonesia',
    value: '62',
  },
  {
    label: 'Italy',
    value: '39',
  },
  {
    label: 'Japan',
    value: '81',
  },
  {
    label: 'Mexico',
    value: '52',
  },
  {
    label: 'Portugal',
    value: '351',
  },
  {
    label: 'South Korea',
    value: '82',
  },
  {
    label: 'Spain',
    value: '34',
  },
  {
    label: 'Sweden',
    value: '46',
  },
  {
    label: 'Switzerland',
    value: '41',
  },
  {
    label: 'UK',
    value: '44',
  },
  {
    label: 'US or Canada',
    value: '1',
  },
  {
    label: 'Vietnam',
    value: '84',
  },
]

export const NOTIFICATION_TYPE_FORM_VALUES = [
  ...Object.values(ENotificationType),
  NOTIFICATION_TYPE_NONE,
] as const

export const NOTIFICATION_TYPE_OPTIONS = [
  {
    label: 'Email',
    value: ENotificationType.email,
  },
  {
    label: 'Text',
    value: ENotificationType.text,
  },
  {
    label: 'Email and text',
    value: ENotificationType.emailAndText,
  },
]
